import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";

import {
  SocketProvider,
  useInterviewSocket,
} from "./SocketProvider";
import apiCall from "../../../utils/axiosCall";
import { endPoint } from "../../../utils/endPoint";
import VideoLayout from "./VideoLayout";
import WaitingOverlay from "./WaitingOverlay";
import logo from "../../../assets/image/logo.png";
import useCandidateRecorder from "./hooks/useCandidateRecorder";
import useInterviewStateMachine, {
  UI_STATE,
} from "./hooks/useInterviewStateMachine";
import useWebRTCConnection from "./hooks/useWebRTCConnection";
import {
  clearFlowSession,
  hasGuidelinesAgreement,
  readBootstrapState,
  readFlowStage,
  saveFlowStage,
} from "./flowStorage";

function normalizeUserType(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "candidate" || normalized === "interviewer") {
    return normalized;
  }
  return "";
}

function normalizeParticipantIdentity(value) {
  return String(value || "").trim().toLowerCase();
}

function normalizeTranscriptUserType(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "staff") {
    return "interviewer";
  }
  if (normalized === "interviewer" || normalized === "candidate") {
    return normalized;
  }
  return "";
}

function toMMSS(seconds) {
  const safe = Math.max(0, Number(seconds || 0));
  const mins = Math.floor(safe / 60)
    .toString()
    .padStart(2, "0");
  const secs = Math.floor(safe % 60)
    .toString()
    .padStart(2, "0");
  return `${mins}:${secs}`;
}

function parseStoredDateTime(value) {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
}

function formatDisplayTime(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatConversationTimestamp(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

function getFullscreenElement() {
  return (
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement
  );
}

async function enterFullscreen() {
  const root = document.documentElement;
  const requestFullscreen =
    root.requestFullscreen ||
    root.webkitRequestFullscreen ||
    root.mozRequestFullScreen ||
    root.msRequestFullscreen;

  if (!requestFullscreen || getFullscreenElement()) {
    return;
  }

  await requestFullscreen.call(root);
}

async function requestInterviewFullscreen(handle, setIsFullScreen) {
  if (handle?.enter) {
    await handle.enter();
    setIsFullScreen?.(true);
    return;
  }

  await enterFullscreen();
  setIsFullScreen?.(Boolean(getFullscreenElement()));
}

function ExternalInterviewRoom({
  roomId,
  email,
  userType,
  sessionId,
  handle,
  isFullScreen,
  setIsFullScreen,
}) {
  const navigate = useNavigate();
  const {
    roomState,
    connectionState,
    socketId,
    sendEvent,
    subscribe,
  } = useInterviewSocket();
  const [turnDiagnostics, setTurnDiagnostics] = useState(null);
  const [callElapsedSeconds, setCallElapsedSeconds] = useState(0);
  const [scheduledTimerSeconds, setScheduledTimerSeconds] = useState(null);
  const [isMicMuted, setIsMicMuted] = useState(false);
  const [remoteMute, setRemoteMute] = useState(false);
  const [microphoneData, setMicrophoneData] = useState(false);
  const [chat, setChat] = useState([]);
  const [pendingTranscript, setPendingTranscript] = useState("");
  const chatContainerRef = useRef(null);
  const finalTranscriptBufferRef = useRef("");
  const lastFinalTranscriptRef = useRef("");
  const finalTranscriptTimeoutRef = useRef(null);
  const endInterviewRef = useRef(false);

  useEffect(() => {
    if (!roomId || !email) {
      return undefined;
    }

    window.history.pushState(null, "", window.location.href);
    const otpRoute = `/external-interview-otp-screen?id=${email}&room=${roomId}`;
    const handlePopState = () => {
      clearFlowSession(roomId, email);
      navigate(otpRoute, { replace: true });
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [email, navigate, roomId]);

  const { uiState, waitingRemainingSeconds, showWaitingActions } =
    useInterviewStateMachine({
      roomState,
      connectionState,
    });

  const { localStream, remoteStream, verifyTurnRelayUsage } = useWebRTCConnection({
    userType,
    roomState,
    connectionState,
    socketId,
    sendEvent,
    subscribe,
  });

  const {
    isRecording,
    isUploading,
    uploadError,
    startRecording,
    stopRecording,
  } = useCandidateRecorder({
    enabled: uiState === UI_STATE.IN_CALL,
    roomId,
    email,
    userType,
  });

  const roleLabel = useMemo(
    () => (userType || "unknown").toUpperCase(),
    [userType]
  );
  const normalizedUserType = useMemo(
    () =>
      normalizeTranscriptUserType(localStorage.getItem("userType")) ||
      normalizeTranscriptUserType(userType),
    [userType]
  );
  const staffName = localStorage.getItem("staff_name") || "Interviewer";
  const candidateName = localStorage.getItem("user_name") || "Candidate";
  const staffLogo = localStorage.getItem("staff_logo") || "RI";
  const candidateLogo = localStorage.getItem("user_logo") || "CA";
  const scheduledStartDateTime = useMemo(
    () => parseStoredDateTime(localStorage.getItem("externalInterviewStartDateTime")),
    []
  );
  const scheduledEndDateTime = useMemo(
    () => parseStoredDateTime(localStorage.getItem("externalInterviewEndDateTime")),
    []
  );
  const otherParticipantType = normalizedUserType === "candidate" ? "interviewer" : "candidate";
  const {
    finalTranscript,
    interimTranscript,
    resetTranscript,
    listening,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  const endInterviewBusinessSession = useCallback(async () => {
    if (!sessionId) {
      return;
    }
    const payload = { session_id: sessionId };
    console.log(`[${roleLabel}][END_INTERVIEW_API_SENT]`, payload);
    await apiCall({
      url: endPoint.EXTERNAL_INTERVIEW_END_INTERVIEW,
      method: "post",
      data: JSON.stringify(payload),
    });
  }, [roleLabel, sessionId]);

  const completeInterview = useCallback(() => {
    saveFlowStage(roomId, email, "completed");
    if (userType === "interviewer") {
      navigate("/feedback/", {
        replace: true,
        state: { from: "External-interview" },
      });
      return;
    }

    navigate("/thank-you/", {
      replace: true,
      state: { from: "External-interview" },
    });
  }, [email, navigate, roomId, userType]);

  const handleEndInterview = useCallback(async () => {
    if (endInterviewRef.current) {
      return;
    }

    endInterviewRef.current = true;

    try {
      sendEvent("room:end", {});
      SpeechRecognition.stopListening();
      stopRecording();
      await endInterviewBusinessSession();
    } catch (error) {
      console.log(`[${roleLabel}][END_INTERVIEW_FLOW_ERROR]`, error);
    } finally {
      completeInterview();
    }
  }, [completeInterview, endInterviewBusinessSession, roleLabel, sendEvent, stopRecording]);

  useEffect(() => {
    if (!localStream) {
      setIsMicMuted(true);
      return;
    }

    const audioTracks = localStream.getAudioTracks();
    if (!audioTracks.length) {
      setIsMicMuted(true);
      return;
    }

    const hasEnabledTrack = audioTracks.some((track) => track.enabled);
    setIsMicMuted(!hasEnabledTrack);
  }, [localStream]);

  const setMicrophoneMuted = useCallback((shouldMute) => {
    if (!localStream) {
      setIsMicMuted(true);
      return;
    }

    const audioTracks = localStream.getAudioTracks();
    if (!audioTracks.length) {
      setIsMicMuted(true);
      return;
    }

    audioTracks.forEach((track) => {
      track.enabled = !shouldMute;
    });

    const hasEnabledTrack = audioTracks.some((track) => track.enabled);
    const actualMuted = !hasEnabledTrack;
    setIsMicMuted(actualMuted);
    sendEvent("mute:state", {
      isMuted: actualMuted,
    });
  }, [localStream, sendEvent]);

  const appendChatMessage = useCallback(({
    type,
    message,
    createdAt,
    transcriptId,
  }) => {
    const safeMessage = String(message || "").trim();
    if (!safeMessage) {
      return;
    }

    const normalizedType = normalizeTranscriptUserType(type) || otherParticipantType;
    setChat((previous) => {
      const nextMessage = {
        type: normalizedType,
        message: safeMessage,
        transcriptId:
          transcriptId ||
          `local-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        createdAt: createdAt || formatDisplayTime(),
      };

      const lastMessage = previous[previous.length - 1];
      if (
        lastMessage &&
        lastMessage.message === nextMessage.message &&
        lastMessage.type === nextMessage.type &&
        !transcriptId &&
        String(lastMessage.transcriptId || "").startsWith("local-")
      ) {
        return previous;
      }

      return [...previous, nextMessage];
    });
  }, [otherParticipantType]);

  const flushFinalTranscript = useCallback(async () => {
    const transcriptText = String(finalTranscriptBufferRef.current || "").trim();
    if (!transcriptText) {
      return;
    }

    finalTranscriptBufferRef.current = "";
    lastFinalTranscriptRef.current = "";
    if (finalTranscriptTimeoutRef.current) {
      clearTimeout(finalTranscriptTimeoutRef.current);
      finalTranscriptTimeoutRef.current = null;
    }

    const createdAt = formatDisplayTime();
    appendChatMessage({
      type: normalizedUserType,
      message: transcriptText,
      createdAt,
    });
    setPendingTranscript("");

    sendEvent("send:transcript", {
      transcript: transcriptText,
      user_type: normalizedUserType,
      createdAt,
      timestamp: formatConversationTimestamp(new Date()),
      session_id: sessionId,
    });

    if (sessionId) {
      const payload = {
        session_id: sessionId,
        text: transcriptText,
        user_type: normalizedUserType === "interviewer" ? "staff" : "candidate",
        timestamp: formatConversationTimestamp(new Date()),
      };

      try {
        await apiCall({
          url:
            endPoint.EXTERNAL_INTERVIEW_V2_UPDATE_ANSWER ||
            endPoint.EXTERNAL_INTERVIEW_UPDATE_ANSWER,
          method: "post",
          data: JSON.stringify(payload),
        });
      } catch (error) {
        console.log(`[${roleLabel}][UPDATE_ANSWER_ERROR]`, error);
      }
    }

    resetTranscript();
  }, [appendChatMessage, normalizedUserType, resetTranscript, roleLabel, sendEvent, sessionId]);

  const audioOff = useCallback(() => {
    setMicrophoneMuted(true);
    setMicrophoneData(false);
    SpeechRecognition.stopListening();
    flushFinalTranscript().catch((error) => {
      console.log(`[${roleLabel}][FINAL_TRANSCRIPT_FLUSH_ERROR]`, error);
    });
  }, [flushFinalTranscript, roleLabel, setMicrophoneMuted]);

  const listenContinuously = useCallback(() => {
    setMicrophoneMuted(false);

    if (!browserSupportsSpeechRecognition) {
      setMicrophoneData(false);
      console.log(`[${roleLabel}][SPEECH_NOT_SUPPORTED]`);
      return;
    }

    setMicrophoneData(true);
    SpeechRecognition.startListening({
      continuous: true,
      interimResults: true,
      language: "en",
    });
  }, [browserSupportsSpeechRecognition, roleLabel, setMicrophoneMuted]);

  useEffect(() => {
    if (userType !== "candidate") {
      return;
    }

    if (uiState === UI_STATE.IN_CALL) {
      startRecording();
      return;
    }

    stopRecording();
  }, [startRecording, stopRecording, uiState, userType]);

  const fetchCurrentConversation = useCallback(async () => {
    if (!sessionId) {
      setChat([]);
      return;
    }

    try {
      const apiResponse = await apiCall({
        url:
          endPoint.EXTERNAL_INTERVIEW_V2_FETCH_CURRENT_CONVERSATION ||
          endPoint.EXTERNAL_INTERVIEW_FETCH_CURRENT_CONVERSATION,
        method: "post",
        data: JSON.stringify({ session_id: sessionId }),
      });

      const conversationData = apiResponse?.result?.conversation_data;
      if (!Array.isArray(conversationData)) {
        setChat([]);
        return;
      }

      const updatedChat = conversationData.map((item) => ({
        type: normalizeTranscriptUserType(item?.user_type),
        message: item?.text || "",
        transcriptId: item?.transcript_id,
        createdAt: item?.formatted_timestamp || "",
      }));
      setChat(updatedChat);
    } catch (error) {
      console.log(`[${roleLabel}][FETCH_CURRENT_CONVERSATION_ERROR]`, error);
    }
  }, [roleLabel, sessionId]);

  useEffect(() => {
    fetchCurrentConversation();
    const intervalId = setInterval(fetchCurrentConversation, 5000);
    return () => clearInterval(intervalId);
  }, [fetchCurrentConversation]);

  useEffect(() => {
    const unsubscribeTranscript = subscribe("send:transcript", (payload) => {
      const transcriptText = String(
        payload?.transcript || payload?.message || payload?.text || ""
      ).trim();

      if (!transcriptText) {
        return;
      }

      const incomingType =
        normalizeTranscriptUserType(payload?.user_type) || otherParticipantType;
      appendChatMessage({
        type: incomingType,
        message: transcriptText,
        createdAt: payload?.createdAt || payload?.formatted_timestamp || formatDisplayTime(),
        transcriptId: payload?.transcript_id,
      });
    });

    const unsubscribeMuteState = subscribe("mute:state", (payload) => {
      setRemoteMute(Boolean(payload?.isMuted));
    });

    return () => {
      unsubscribeTranscript();
      unsubscribeMuteState();
    };
  }, [appendChatMessage, otherParticipantType, subscribe]);

  useEffect(() => {
    const unsubscribeRoomEnded = subscribe("room:ended", () => {
      completeInterview();
    });

    return () => {
      unsubscribeRoomEnded();
    };
  }, [completeInterview, subscribe]);

  useEffect(() => {
    if (!remoteStream) {
      return;
    }

    remoteStream.getAudioTracks().forEach((track) => {
      track.enabled = !remoteMute;
    });
  }, [remoteMute, remoteStream]);

  useEffect(() => {
    if (!finalTranscript || !finalTranscript.trim()) {
      return;
    }

    const transcriptText = finalTranscript.trim();
    const previousFinalTranscript = lastFinalTranscriptRef.current;
    lastFinalTranscriptRef.current = transcriptText;

    let newTranscriptText = transcriptText;
    if (previousFinalTranscript && transcriptText === previousFinalTranscript) {
      return;
    }
    if (
      previousFinalTranscript &&
      transcriptText.startsWith(previousFinalTranscript)
    ) {
      newTranscriptText = transcriptText
        .slice(previousFinalTranscript.length)
        .trim();
    }

    if (!newTranscriptText) {
      return;
    }

    finalTranscriptBufferRef.current = [
      finalTranscriptBufferRef.current,
      newTranscriptText,
    ]
      .filter(Boolean)
      .join(" ");
    setPendingTranscript(finalTranscriptBufferRef.current);

    if (finalTranscriptTimeoutRef.current) {
      clearTimeout(finalTranscriptTimeoutRef.current);
    }
    finalTranscriptTimeoutRef.current = setTimeout(() => {
      flushFinalTranscript().catch((error) => {
        console.log(`[${roleLabel}][FINAL_TRANSCRIPT_TIMER_ERROR]`, error);
      });
    }, 5000);
  }, [finalTranscript, flushFinalTranscript, roleLabel]);

  const liveInterimTranscript = String(interimTranscript || "").trim();
  const liveTranscript =
    liveInterimTranscript || String(pendingTranscript || "").trim();

  const togglePageFullscreen = useCallback(() => {
    const fullscreenElement = getFullscreenElement();
    if (fullscreenElement) {
      const exitFullscreen =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.mozCancelFullScreen ||
        document.msExitFullscreen;

      if (!exitFullscreen) {
        return;
      }

      Promise.resolve(exitFullscreen.call(document))
        .then(() => {
          setIsFullScreen?.(false);
        })
        .catch((error) => {
          console.log(`[${roleLabel}][FULLSCREEN_EXIT_ERROR]`, error);
        });
      return;
    }

    requestInterviewFullscreen(handle, setIsFullScreen)
      .catch((error) => {
        console.log(`[${roleLabel}][FULLSCREEN_REQUEST_ERROR]`, error);
      });
  }, [handle, roleLabel, setIsFullScreen]);

  useEffect(() => {
    if (uiState !== UI_STATE.IN_CALL) {
      audioOff();
      return;
    }

    if (!isMicMuted && !listening) {
      listenContinuously();
    }
  }, [audioOff, isMicMuted, listening, listenContinuously, uiState]);

  useEffect(() => {
    return () => {
      if (finalTranscriptTimeoutRef.current) {
        clearTimeout(finalTranscriptTimeoutRef.current);
      }
      SpeechRecognition.stopListening();
    };
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullScreen?.(Boolean(getFullscreenElement()));
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    handleFullscreenChange();

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
    };
  }, [setIsFullScreen]);

  useEffect(() => {
    if (uiState !== UI_STATE.IN_CALL) {
      setCallElapsedSeconds(0);
      return undefined;
    }

    const startedAt = Date.now();
    const tick = () => {
      const elapsed = Math.floor((Date.now() - startedAt) / 1000);
      setCallElapsedSeconds(elapsed);
    };
    tick();

    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [uiState]);

  useEffect(() => {
    if (!scheduledStartDateTime || !scheduledEndDateTime) {
      setScheduledTimerSeconds(null);
      return undefined;
    }

    const totalScheduledSeconds = Math.max(
      0,
      Math.floor((scheduledEndDateTime.getTime() - scheduledStartDateTime.getTime()) / 1000)
    );

    const tick = () => {
      if (uiState === UI_STATE.IN_CALL) {
        const remainingSeconds = Math.max(
          0,
          Math.floor((scheduledEndDateTime.getTime() - Date.now()) / 1000)
        );
        setScheduledTimerSeconds(remainingSeconds);
        return;
      }

      setScheduledTimerSeconds(totalScheduledSeconds);
    };

    tick();
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [scheduledEndDateTime, scheduledStartDateTime, uiState]);

  useEffect(() => {
    if (uiState === UI_STATE.ENDED) {
      completeInterview();
    }
  }, [completeInterview, uiState]);

  useEffect(() => {
    if (!chatContainerRef.current) {
      return;
    }
    chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
  }, [chat, liveTranscript]);

  const participantSummary = roomState?.participants?.map((participant) => {
    return `${participant.user_type}:${participant.state}`;
  });
  const latestSubtitle =
    chat.length > 0
      ? chat[chat.length - 1]?.message
      : "";
  const timerLabel = toMMSS(
    scheduledTimerSeconds !== null ? scheduledTimerSeconds : callElapsedSeconds
  );
  const timerVariant =
    scheduledTimerSeconds !== null && scheduledTimerSeconds <= 60
      ? "danger"
      : scheduledTimerSeconds !== null && scheduledTimerSeconds <= 300
        ? "warning"
        : "default";

  useEffect(() => {
    if (!scheduledEndDateTime || scheduledTimerSeconds !== 0) {
      return;
    }

    if (uiState === UI_STATE.ENDED) {
      return;
    }

    handleEndInterview().catch((error) => {
      console.log(`[${roleLabel}][AUTO_END_INTERVIEW_ERROR]`, error);
    });
  }, [
    handleEndInterview,
    roleLabel,
    scheduledEndDateTime,
    scheduledTimerSeconds,
    uiState,
  ]);

  return (
    <div className="min-h-screen bg-white">
      <header className="flex h-[88px] items-center px-5 shadow-[0_4px_6px_-1px_rgba(13,16,45,0.1)] sm:px-[60px]">
        <img className="h-8 w-auto" src={logo} alt="Resourceful" />
      </header>

      <div className="relative flex flex-col gap-6 p-4 sm:p-6 lg:h-[calc(100vh-88px)] lg:flex-row">
        <VideoLayout
          localStream={localStream}
          remoteStream={remoteStream}
          uiState={uiState}
          timerLabel={timerLabel}
          timerVariant={timerVariant}
          subtitleText={latestSubtitle}
          isMicMuted={isMicMuted}
          isFullscreen={Boolean(isFullScreen)}
          remoteMute={remoteMute}
          controlsDisabled={uiState !== UI_STATE.IN_CALL}
          onToggleMute={() => {
            if (isMicMuted) {
              listenContinuously();
              return;
            }
            audioOff();
          }}
          onToggleFullscreen={togglePageFullscreen}
          onLeave={() => {
            handleEndInterview().catch((error) => {
              console.log(`[${roleLabel}][END_CALL_BUTTON_ERROR]`, error);
            });
          }}
        />

        <div className="flex h-[400px] w-full flex-col overflow-hidden rounded-2xl border border-[#B1CDF8] bg-[linear-gradient(0deg,rgba(140,180,244,0.08),rgba(140,180,244,0.08)),#fff] lg:h-auto lg:w-[403px]">
          <div className="border-b border-[#B1CDF8] p-4">
            <div className="text-lg font-bold">Transcript</div>
            <div className="mt-1 hidden text-xs text-[#4B5A74]">
              {roleLabel} | room={roomId} | socket={connectionState}
            </div>
          </div>

          <div ref={chatContainerRef} className="flex-1 overflow-y-auto p-4">
            {chat.map((item, index) => {
              const speakerType = item.type || "interviewer";
              const isSelf = speakerType === normalizedUserType;
              const speakerName = speakerType === "interviewer" ? staffName : candidateName;
              const speakerLogo = speakerType === "interviewer" ? staffLogo : candidateLogo;

              return (
                <div
                  key={item.transcriptId || `${item.createdAt}-${index}`}
                  className={`mb-[18px] flex gap-[6px] ${isSelf ? "justify-end" : ""}`}
                >
                  {!isSelf ? (
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#045DE8] text-sm font-semibold text-white">
                      {speakerLogo}
                    </div>
                  ) : null}

                  <div className="max-w-[237px]">
                    <div
                      className={`mb-1 text-[10px] text-[#6b7280] ${
                        isSelf ? "text-right" : "text-left"
                      }`}
                    >
                      {speakerName}
                      {item.createdAt ? <span className="ml-1">{item.createdAt}</span> : null}
                    </div>
                    <div
                      className={`px-[10px] py-2 text-[10px] shadow-[0_1px_4px_rgba(0,0,0,0.08)] ${
                        isSelf
                          ? "rounded-[8px_12px_0px_8px] bg-[#367DED] text-white"
                          : "rounded-[12px_8px_8px_0px] bg-white text-[#313134]"
                      }`}
                    >
                      {item.message || "..."}
                    </div>
                  </div>

                  {isSelf ? (
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#045DE8] text-sm font-semibold text-white">
                      {speakerLogo}
                    </div>
                  ) : null}
                </div>
              );
            })}

            {!chat.length && !liveTranscript ? (
              <div className="rounded-[12px] bg-white px-3 py-2 text-[11px] text-[#313134] shadow-[0_1px_4px_rgba(0,0,0,0.08)]">
                Transcript will appear here once conversation starts.
              </div>
            ) : null}

            {liveTranscript ? (
              <div className="mb-[18px] flex justify-end gap-[6px]">
                <div className="max-w-[237px]">
                  <div className="mb-1 text-right text-[10px] text-[#6b7280]">
                    {normalizedUserType === "interviewer" ? staffName : candidateName}
                    {liveInterimTranscript ? (
                      <span className="ml-1">Speaking...</span>
                    ) : null}
                  </div>
                  <div className="rounded-[8px_12px_0px_8px] bg-[#367DED]/80 px-[10px] py-2 text-[10px] text-white shadow-[0_1px_4px_rgba(0,0,0,0.08)]">
                    {liveTranscript}
                  </div>
                </div>
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#045DE8] text-sm font-semibold text-white">
                  {normalizedUserType === "interviewer" ? staffLogo : candidateLogo}
                </div>
              </div>
            ) : null}

            <div className="rounded-xl hidden bg-white/85 p-3 text-[11px] leading-5 text-[#1A2A43] shadow-[0_1px_4px_rgba(0,0,0,0.08)]">
              <div>
                <strong>Server Room Status:</strong> {roomState?.status || "unknown"}
              </div>
              <div>
                <strong>Participants:</strong> {(participantSummary || []).join(" | ") || "none"}
              </div>
              <div>
                <strong>Candidate Recording:</strong> {isRecording ? "recording" : "stopped"}
                {isUploading ? " | uploading" : ""}
                {uploadError ? " | error" : ""}
              </div>
              <div>
                <strong>Microphone:</strong> {isMicMuted ? "muted" : "unmuted"}
              </div>
              <div>
                <strong>Speech Recognition:</strong> {listening ? "listening" : "stopped"}
              </div>
              <div>
                <strong>Mic Input State:</strong> {microphoneData ? "active" : "idle"}
              </div>
              <div>
                <strong>Remote Microphone:</strong> {remoteMute ? "muted" : "unmuted"}
              </div>
              <div>
                <strong>Socket Id:</strong> {socketId || "not assigned"}
              </div>
              <button
                type="button"
                onClick={async () => {
                  const diagnostics = await verifyTurnRelayUsage();
                  setTurnDiagnostics(diagnostics);
                  console.log(`[${roleLabel}][TURN_DIAGNOSTICS]`, diagnostics);
                }}
                className="mt-2 rounded-md border border-[#1f4f82] bg-white px-2 py-1 text-[11px] font-medium text-[#1f4f82]"
              >
                Verify TURN
              </button>
            </div>

            {turnDiagnostics ? (
              <pre className="mt-3 overflow-x-auto rounded-xl bg-[#0b1d30] p-3 text-[11px] text-[#b8e0ff]">
                {JSON.stringify(turnDiagnostics, null, 2)}
              </pre>
            ) : null}
          </div>
        </div>

        {roomState && [UI_STATE.WAITING, UI_STATE.RECONNECTING].includes(uiState) ? (
          <WaitingOverlay
            uiState={uiState}
            waitingRemainingSeconds={waitingRemainingSeconds}
            waitingCycle={roomState.waiting_cycle}
            showWaitingActions={showWaitingActions}
            onEndInterview={() => {
              handleEndInterview().catch((error) => {
                console.log(`[${roleLabel}][WAITING_END_INTERVIEW_ERROR]`, error);
              });
            }}
            onWaitMore={() => {
              sendEvent("room:wait:extend", {});
            }}
          />
        ) : null}
      </div>
    </div>
  );
}

export default function ExternalInterview({ handle, isFullScreen, setIsFullScreen }) {
  const navigate = useNavigate();
  const { roomId } = useParams();
  const [searchParams] = useSearchParams();
  const bootstrapState = readBootstrapState();
  const bootstrapForCurrentRoom =
    bootstrapState && bootstrapState.roomId === roomId ? bootstrapState : null;

  const participantIdentity = normalizeParticipantIdentity(
    searchParams.get("email") ||
      searchParams.get("userId") ||
      bootstrapForCurrentRoom?.userId
  );
  const userTypeFromQuery = normalizeUserType(
    searchParams.get("userType") ||
      searchParams.get("role") ||
      bootstrapForCurrentRoom?.userType
  );
  const sessionIdFromQuery =
    searchParams.get("sessionId") || bootstrapForCurrentRoom?.sessionId || "";
  const userIdFromQuery = normalizeParticipantIdentity(
    searchParams.get("userId") ||
      searchParams.get("email") ||
      bootstrapForCurrentRoom?.userId
  );
  const isGuidelinesAgreed =
    Boolean(roomId) &&
    Boolean(participantIdentity) &&
    hasGuidelinesAgreement(roomId, participantIdentity);
  const currentFlowStage = readFlowStage(roomId, participantIdentity);

  useEffect(() => {
    if (!roomId || !participantIdentity || !userTypeFromQuery) {
      return;
    }
    if (currentFlowStage === "completed") {
      navigate(
        userTypeFromQuery === "interviewer" ? "/feedback/" : "/thank-you/",
        {
          replace: true,
          state: { from: "External-interview" },
        }
      );
      return;
    }
    if (isGuidelinesAgreed) {
      saveFlowStage(roomId, participantIdentity, "interview");
      return;
    }
    const query = new URLSearchParams({
      userId: participantIdentity,
      userType: userTypeFromQuery,
      sessionId: sessionIdFromQuery || "",
    });
    navigate(`/external-interview-v2-guidelines/${roomId}?${query.toString()}`, {
      replace: true,
    });
  }, [
    currentFlowStage,
    isGuidelinesAgreed,
    navigate,
    participantIdentity,
    roomId,
    sessionIdFromQuery,
    userTypeFromQuery,
  ]);

  if (participantIdentity) {
    localStorage.setItem("externalInterviewEmail", participantIdentity);
  }
  if (userTypeFromQuery) {
    localStorage.setItem("externalInterviewRole", userTypeFromQuery);
  }
  if (sessionIdFromQuery) {
    localStorage.setItem("session_id", sessionIdFromQuery);
  }
  if (roomId) {
    localStorage.setItem("resourceful_video_call_room", roomId);
  }
  if (userIdFromQuery) {
    localStorage.setItem("resourceful_video_call_id", userIdFromQuery);
  }

  if (!roomId || !participantIdentity || !userTypeFromQuery) {
    return (
      <div style={{ padding: 20 }}>
        Missing required params. Use:
        <pre>
          /external-interview-v2/{"<roomId>"}?userId={"<uuid>"}&userType=candidate
        </pre>
      </div>
    );
  }

  if (!isGuidelinesAgreed) {
    return null;
  }

  return (
    <SocketProvider roomId={roomId} email={participantIdentity} userType={userTypeFromQuery}>
      <ExternalInterviewRoom
        roomId={roomId}
        email={participantIdentity}
        userType={userTypeFromQuery}
        sessionId={sessionIdFromQuery}
        handle={handle}
        isFullScreen={isFullScreen}
        setIsFullScreen={setIsFullScreen}
      />
    </SocketProvider>
  );
}
